import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";

export default function ForcePasswordChange() {
    const { user, setUser } = useAuth();
    const open = !!(user && user.must_change_password);
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (form.new_password !== form.confirm) return toast.error("Passwords do not match");
        if (form.new_password.length < 8) return toast.error("Minimum 8 characters");
        setBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current_password, new_password: form.new_password,
            });
            toast.success("Password changed");
            setUser({ ...user, must_change_password: false });
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open}>
            <DialogContent className="max-w-md" onEscapeKeyDown={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()} data-testid="force-password-modal">
                <DialogHeader>
                    <DialogTitle className="font-serif text-komma-navy flex items-center gap-2">
                        <ShieldAlert size={18} className="text-komma-gold" /> Set your permanent password
                    </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-gray-600">
                    You are using a temporary password issued by your CEO or Office Admin. Please choose a permanent password to continue.
                </p>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-komma-navy">Current (temporary) password</Label>
                        <Input required type="password" value={form.current_password}
                            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                            className="mt-1.5 h-11" data-testid="fpw-current" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-komma-navy">New password (min 8)</Label>
                        <Input required type="password" minLength={8} value={form.new_password}
                            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                            className="mt-1.5 h-11" data-testid="fpw-new" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-komma-navy">Confirm new password</Label>
                        <Input required type="password" minLength={8} value={form.confirm}
                            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                            className="mt-1.5 h-11" data-testid="fpw-confirm" />
                    </div>
                    <Button type="submit" disabled={busy} className="w-full bg-komma-navy hover:bg-komma-navy-light" data-testid="fpw-submit">
                        {busy ? "Saving…" : "Set new password"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
